/**
 * Fechamentos (apuração do cliente) — tela admin EUN.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { CalendarRange, CheckCircle2, Copy, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  CLOSING_STATUS_LABEL,
  closingsApi,
  type Closing,
} from "@/lib/api-closings";
import { formatCurrency, formatDate } from "@/lib/format";

const STATUS_VARIANT: Record<string, string> = {
  draft: "muted",
  sent: "info",
  under_review: "warning",
  closed_by_client: "primary",
  adjusted: "purple",
  finalized: "success",
  cancelled: "danger",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={(STATUS_VARIANT[status] ?? "default") as never} dot>
      {CLOSING_STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

export function ClosingsPage() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["closings"],
    queryFn: () => closingsApi.list({ size: 200 }),
  });

  const detail = useQuery({
    queryKey: ["closing", openId],
    queryFn: () => closingsApi.get(openId!),
    enabled: !!openId,
  });

  const generate = useMutation({
    mutationFn: () => {
      const now = new Date();
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return closingsApi.generate(prev.getFullYear(), prev.getMonth() + 1);
    },
    onSuccess: (r) => {
      toast.success(`Apurações geradas: ${r.created} (${r.skipped_existing} já existiam)`);
      qc.invalidateQueries({ queryKey: ["closings"] });
    },
    onError: () => toast.error("Falha ao gerar apurações"),
  });

  const refetchBoth = () => {
    qc.invalidateQueries({ queryKey: ["closings"] });
    qc.invalidateQueries({ queryKey: ["closing", openId] });
  };

  const send = useMutation({
    mutationFn: (id: string) => closingsApi.send(id),
    onSuccess: () => {
      toast.success("E-mail enviado ao cliente");
      refetchBoth();
    },
    onError: () => toast.error("Falha ao enviar e-mail"),
  });

  const reconcile = useMutation({
    mutationFn: (c: Closing) =>
      closingsApi.reconcile(c.id, (c.items ?? []).map((i) => i.id)),
    onSuccess: () => {
      toast.success("Contestações aceitas (itens reconhecidos)");
      refetchBoth();
    },
  });

  const finalize = useMutation({
    mutationFn: (id: string) => closingsApi.finalize(id),
    onSuccess: () => {
      toast.success("Fechamento finalizado");
      refetchBoth();
    },
  });

  const copyLink = async (id: string) => {
    const { url } = await closingsApi.link(id);
    await navigator.clipboard.writeText(url);
    toast.success("Link de conferência copiado");
  };

  const columns: ColumnDef<Closing>[] = [
    {
      id: "client",
      header: "Cliente",
      cell: ({ row }) => <span className="font-medium">{row.original.client_name ?? "—"}</span>,
    },
    {
      id: "period",
      header: "Período",
      cell: ({ row }) => (
        <span className="tabular-nums">
          {String(row.original.month).padStart(2, "0")}/{row.original.year}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: "gross",
      header: "Bruto",
      cell: ({ row }) => <span className="tabular-nums">{formatCurrency(row.original.gross_total)}</span>,
    },
    {
      id: "recognized",
      header: "Reconhecido",
      cell: ({ row }) => (
        <span className="tabular-nums text-emerald-600">{formatCurrency(row.original.recognized_total)}</span>
      ),
    },
    {
      id: "disputed",
      header: "Contestado",
      cell: ({ row }) => {
        const v = parseFloat(row.original.disputed_total);
        return v > 0 ? (
          <span className="tabular-nums text-rose-600">{formatCurrency(v)}</span>
        ) : (
          <span className="text-slate-400">—</span>
        );
      },
    },
    {
      id: "items",
      header: "Itens",
      cell: ({ row }) => <span className="tabular-nums">{row.original.item_count}</span>,
    },
  ];

  const c = detail.data;

  return (
    <>
      <PageHeader
        title="Fechamentos"
        description="Apuração mensal de faturamento por cliente — conferência e conciliação"
        actions={
          <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
            <CalendarRange className="h-4 w-4" /> Gerar mês anterior
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        onRowClick={(row) => setOpenId(row.id)}
      />

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>
              {c ? `${c.client_name} · ${String(c.month).padStart(2, "0")}/${c.year}` : "Fechamento"}
            </SheetTitle>
          </SheetHeader>
          <SheetBody>
            {detail.isLoading || !c ? (
              <p className="text-sm text-muted-foreground">Carregando…</p>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <StatusBadge status={c.status} />
                  {c.closed_by_name && (
                    <span className="text-xs text-muted-foreground">
                      Fechado por {c.closed_by_name}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="rounded-lg border p-2">
                    <div className="text-xs text-muted-foreground">Bruto</div>
                    <div className="font-semibold tabular-nums">{formatCurrency(c.gross_total)}</div>
                  </div>
                  <div className="rounded-lg border p-2">
                    <div className="text-xs text-muted-foreground">Reconhecido</div>
                    <div className="font-semibold tabular-nums text-emerald-600">
                      {formatCurrency(c.recognized_total)}
                    </div>
                  </div>
                  <div className="rounded-lg border p-2">
                    <div className="text-xs text-muted-foreground">Contestado</div>
                    <div className="font-semibold tabular-nums text-rose-600">
                      {formatCurrency(c.disputed_total)}
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  {(c.items ?? []).map((it) => (
                    <div
                      key={it.id}
                      className={`flex items-center justify-between rounded-lg border p-2 text-sm ${
                        it.recognized ? "" : "border-rose-200 bg-rose-50"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium">{it.description}</div>
                        <div className="text-xs text-muted-foreground">
                          {it.job_code}
                          {it.scheduled_at && ` · ${formatDate(it.scheduled_at)}`}
                          {!it.recognized && it.dispute_reason && ` · ✕ ${it.dispute_reason}`}
                        </div>
                      </div>
                      <span className="tabular-nums whitespace-nowrap">{formatCurrency(it.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SheetBody>
          {c && (
            <SheetFooter className="flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => copyLink(c.id)}>
                <Copy className="h-4 w-4" /> Copiar link
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => send.mutate(c.id)}
                disabled={send.isPending}
              >
                <Send className="h-4 w-4" /> {c.sent_at ? "Reenviar" : "Enviar"} e-mail
              </Button>
              {parseFloat(c.disputed_total) > 0 && c.status !== "finalized" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => reconcile.mutate(c)}
                  disabled={reconcile.isPending}
                >
                  Aceitar contestações
                </Button>
              )}
              {c.status !== "finalized" && (
                <Button
                  size="sm"
                  onClick={() => finalize.mutate(c.id)}
                  disabled={finalize.isPending}
                >
                  <CheckCircle2 className="h-4 w-4" /> Finalizar
                </Button>
              )}
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
